import React, { useState } from 'react';
import EventSimulator from './EventSimulator';
import WorkflowBuilder from './WorkflowBuilder';
import Analytics from './Analytics';
import ResponseCustomizer from './ResponseCustomizer';
import IntegrationHub from './IntegrationHub';

const SlackExplorer = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [connectionStatus, setConnectionStatus] = useState({
    slack: false,
    webhooks: false,
    socketMode: false,
    enterpriseGrid: false
  });

  const tabs = [
    { id: 'overview', name: 'Overview', icon: '🏠' },
    { id: 'events', name: 'Event Simulator', icon: '⚡' },
    { id: 'workflows', name: 'Workflow Builder', icon: '🔄' },
    { id: 'analytics', name: 'Analytics', icon: '📊' },
    { id: 'responses', name: 'AI Responses', icon: '🤖' },
    { id: 'integrations', name: 'Integrations', icon: '🔌' }
  ];

  const features = [
    {
      name: 'Voice Commands',
      status: 'ready',
      description: 'Send voice messages to Claude via Slack',
      capabilities: ['Voice transcription', 'Command recognition', 'Multi-language support']
    },
    {
      name: 'Rich Media Responses',
      status: 'ready',
      description: 'Enhanced message formatting with charts and code',
      capabilities: ['Interactive charts', 'Syntax highlighting', 'Image generation']
    },
    {
      name: 'Scheduled Automations',
      status: 'prototype',
      description: 'Set up recurring tasks and reports',
      capabilities: ['Cron scheduling', 'Report generation', 'Task automation']
    },
    {
      name: 'Context-Aware Responses',
      status: 'ready',
      description: 'Claude remembers conversation history',
      capabilities: ['Thread context', 'User preferences', 'Team knowledge base']
    },
    {
      name: 'Multi-Channel Broadcasting',
      status: 'ready',
      description: 'Send updates to multiple channels simultaneously',
      capabilities: ['Channel targeting', 'Message routing', 'Priority levels']
    },
    {
      name: 'Custom Bot Personas',
      status: 'prototype',
      description: 'Different response styles for different teams',
      capabilities: ['Personality profiles', 'Team customization', 'Response templates']
    },
    {
      name: 'Slack Canvas Integration',
      status: 'experimental',
      description: 'Create and update canvases programmatically',
      capabilities: ['Canvas creation', 'Content updates', 'Collaboration features']
    },
    {
      name: 'Huddle Integration',
      status: 'experimental',
      description: 'Join huddles and provide real-time assistance',
      capabilities: ['Audio participation', 'Screen sharing', 'Live transcription']
    }
  ];

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Connection Status */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-green-400 mb-4">Connection Status</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(connectionStatus).map(([key, connected]) => (
            <div key={key} className="bg-black p-3 rounded border border-gray-600">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400 capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </span>
                <div className={`w-3 h-3 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'} animate-pulse`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Feature Grid */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-green-400 mb-4">Advanced Features</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {features.map((feature) => (
            <div key={feature.name} className="bg-black p-4 rounded border border-gray-600">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-bold text-yellow-400">{feature.name}</h4>
                <span className={`text-xs px-2 py-1 rounded ${
                  feature.status === 'ready' ? 'bg-green-900 text-green-300' :
                  feature.status === 'prototype' ? 'bg-yellow-900 text-yellow-300' :
                  'bg-purple-900 text-purple-300'
                }`}>
                  {feature.status}
                </span>
              </div>
              <p className="text-sm text-gray-400 mb-2">{feature.description}</p>
              <div className="space-y-1">
                {feature.capabilities.map((cap, idx) => (
                  <div key={idx} className="text-xs text-gray-500">
                    • {cap}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* API Capabilities */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-green-400 mb-4">Slack API Capabilities</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-black p-4 rounded border border-gray-600">
            <h4 className="font-bold text-blue-400 mb-2">Socket Mode</h4>
            <p className="text-sm text-gray-400">Real-time bidirectional communication without public URLs</p>
          </div>
          <div className="bg-black p-4 rounded border border-gray-600">
            <h4 className="font-bold text-blue-400 mb-2">App Home</h4>
            <p className="text-sm text-gray-400">Custom tabs and views in Slack app home</p>
          </div>
          <div className="bg-black p-4 rounded border border-gray-600">
            <h4 className="font-bold text-blue-400 mb-2">Workflow Steps</h4>
            <p className="text-sm text-gray-400">Custom steps in Slack Workflow Builder</p>
          </div>
          <div className="bg-black p-4 rounded border border-gray-600">
            <h4 className="font-bold text-blue-400 mb-2">Event Subscriptions</h4>
            <p className="text-sm text-gray-400">Monitor and respond to workspace events</p>
          </div>
          <div className="bg-black p-4 rounded border border-gray-600">
            <h4 className="font-bold text-blue-400 mb-2">External Data</h4>
            <p className="text-sm text-gray-400">Connect external data sources to Slack</p>
          </div>
          <div className="bg-black p-4 rounded border border-gray-600">
            <h4 className="font-bold text-blue-400 mb-2">Enterprise Grid</h4>
            <p className="text-sm text-gray-400">Multi-workspace support for large orgs</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'events':
        return <EventSimulator />;
      case 'workflows':
        return <WorkflowBuilder />;
      case 'analytics':
        return <Analytics />;
      case 'responses':
        return <ResponseCustomizer />;
      case 'integrations':
        return <IntegrationHub />;
      default:
        return renderOverview();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-green-400 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">🚀 Slack Explorer</h1>
          <p className="text-gray-400">Discover advanced Slack integration possibilities</p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-green-600 text-black'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div className="bg-gray-900 rounded-lg p-6 border border-green-400">
          {renderContent()}
        </div>

        {/* Quick Actions */}
        <div className="mt-6 flex gap-4">
          <button className="px-4 py-2 bg-green-600 text-black rounded-lg hover:bg-green-500 transition-colors">
            Test Connection
          </button>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors">
            View Documentation
          </button>
          <button className="px-4 py-2 bg-yellow-600 text-black rounded-lg hover:bg-yellow-500 transition-colors">
            Export Configuration
          </button>
        </div>
      </div>
    </div>
  );
};

export default SlackExplorer;